import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAtualizarPerfil, useAlterarSenhaPerfil } from '../../hooks/usePerfil';
import { usePageTitle } from '../../hooks/usePageTitle';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import toast from 'react-hot-toast';
import { validateRequired, validateEmail, validateMinLength, validatePasswordMatch, combineValidations } from '../../utils/validations';

const Perfil: React.FC = () => {
  usePageTitle('Perfil');
  const { user, updateUser } = useAuth();

  const [nome, setNome] = useState(user?.nome ?? '');
  const [email, setEmail] = useState(user?.email ?? '');

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');

  const { mutate: atualizarPerfil, isPending: salvandoPerfil } = useAtualizarPerfil();
  const { mutate: alterarSenha, isPending: alterandoSenha } = useAlterarSenhaPerfil();

  useEffect(() => {
    if (user) {
      setNome(user.nome);
      setEmail(user.email);
    }
  }, [user]);

  const handleSalvarDados = (e: React.FormEvent) => {
    e.preventDefault();
    atualizarPerfil(
      { nome, email },
      {
        onSuccess: (data) => {
          updateUser({ nome: data.nome, email: data.email });
          toast.success('Dados atualizados com sucesso!');
        },
      }
    );
  };

  const handleAlterarSenha = (e: React.FormEvent) => {
    e.preventDefault();
    alterarSenha(
      { senhaAtual, novaSenha, confirmarNovaSenha },
      {
        onSuccess: (data) => {
          toast.success(data.mensagem);
          setSenhaAtual('');
          setNovaSenha('');
          setConfirmarNovaSenha('');
        },
      }
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-4xl font-display font-bold text-neutral-900">Meu Perfil</h1>
        <p className="text-neutral-500 text-sm mt-1">Gerencie suas informações pessoais</p>
      </div>

      {/* Dados pessoais */}
      <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
        <h2 className="text-lg font-display font-semibold text-neutral-800 mb-4 pb-2 border-b border-neutral-200">
          Dados Pessoais
        </h2>
        <form onSubmit={handleSalvarDados} className="space-y-5">
          <Input
            label="Nome *"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            onValidate={validateRequired('Nome')}
            validateOnBlur
          />

          <Input
            label="E-mail *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onValidate={combineValidations(validateRequired('E-mail'), validateEmail)}
            validateOnBlur
          />

          <div className="pt-4 border-t border-neutral-100">
            <Button type="submit" variant="primary" size="md" disabled={salvandoPerfil}>
              {salvandoPerfil ? 'Salvando...' : 'Salvar Dados'}
            </Button>
          </div>
        </form>
      </div>

      {/* Alterar senha */}
      <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
        <h2 className="text-lg font-display font-semibold text-neutral-800 mb-4 pb-2 border-b border-neutral-200">
          Alterar Senha
        </h2>
        <form onSubmit={handleAlterarSenha} className="space-y-5">
          <Input
            label="Senha Atual *"
            type="password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            onValidate={combineValidations(
              validateRequired('Senha atual'),
              validateMinLength(6, 'Senha atual')
            )}
            validateOnBlur
            autoComplete="current-password"
          />

          <Input
            label="Nova Senha *"
            type="password"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            onValidate={combineValidations(
              validateRequired('Nova senha'),
              validateMinLength(6, 'Nova senha')
            )}
            validateOnBlur
            helperText="Mínimo 6 caracteres"
            autoComplete="new-password"
          />

          <Input
            label="Confirmar Nova Senha *"
            type="password"
            value={confirmarNovaSenha}
            onChange={(e) => setConfirmarNovaSenha(e.target.value)}
            onValidate={combineValidations(
              validateRequired('Confirmação de senha'),
              validatePasswordMatch(novaSenha)
            )}
            validateOnBlur
            validateOnChange
            autoComplete="new-password"
          />

          <div className="pt-4 border-t border-neutral-100">
            <Button type="submit" variant="primary" size="md" disabled={alterandoSenha}>
              {alterandoSenha ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Perfil;
